import React, { useEffect, useRef, useState } from 'react';
import FocusTrap from 'focus-trap-react';
import { MeatballIcon } from '../../../assets/icons';

export interface MenuDropdownOptions {
  name: string;
  icon: Function;
}
interface MenuDropdownProps {
  label: string;
  dataTestId: string;
  initialValue?: string;
  options: Array<MenuDropdownOptions>;
  data: any;
  onSelect: Function;
}

export const MenuDropdown = (props: MenuDropdownProps) => {
  const { label, dataTestId, options, data, initialValue = '...', onSelect } = props;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<string | number | null>(initialValue);
  const [focusIndex, setFocusIndex] = useState<number>(0);

  const focusSelected = useRef<HTMLButtonElement>(null);
  const focusItem = useRef<Array<any>>([]);

  const toggleMenuDropdown = () => {
    setIsOpen(!isOpen);
  };

  const selectItem = (val: string | number | null) => {
    setSelected(val);
    onSelect(val, data);
    setIsOpen(false);
  };

  useEffect(() => {
    if (focusItem.current.length === 1) {
      focusItem.current[0]?.focus();
      setFocusIndex(0);
    }
  }, [focusItem]);

  const keyDownHandler = (e: any) => {
    e.preventDefault();
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
    if (e.key === 'Enter' && !isOpen) {
      setIsOpen(true);
    } else if (e.key === 'Enter' && isOpen) {
      selectItem(e.target.name);
    }
    if (e.key === 'Tab') {
      const nextItem = focusIndex < options.length - 1 ? focusIndex + 1 : 0;
      setFocusIndex(nextItem);
      focusItem.current[nextItem]?.focus();
    }
    if (e.key === 'Tab' && e.shiftKey) {
      const nextItem = focusIndex === 0 ? options.length - 1 : focusIndex - 1;
      setFocusIndex(nextItem);
      focusItem.current[nextItem]?.focus();
    }
  };

  return (
    <div className="dropdown w-full relative" data-testid={'menu-dropdown-container-' + dataTestId}>
      <button
        ref={focusSelected}
        className={'w-full p-3 rounded ' + (isOpen ? 'bg-dhs-gray-20' : '')}
        type="button"
        id="menu-dropdown-toggle-button"
        data-bs-toggle="dropdown"
        aria-label={`${label} ${selected} - ${isOpen ? 'expanded' : 'collapsed'}`}
        onClick={toggleMenuDropdown}
      >
        <div className="flex justify-center" data-testid={'menu-dropdown-' + dataTestId}>
          <MeatballIcon />
        </div>
      </button>
      {isOpen && (
        <FocusTrap
          focusTrapOptions={{
            clickOutsideDeactivates: true,
            onDeactivate: () => {
              setIsOpen(false);
            },
          }}
        >
          <div data-testid={'menu-dropdown-options-' + dataTestId}>
            <div
              className="dropdown-menu w-full absolute bg-white text-base z-50 float-center list-none text-center shadow-lg mt-1 m-0 bg-clip-padding border-none rounded-lg"
              aria-labelledby="dropdownMenuMediumButton"
            >
              {options.map((option, idx) => (
                <button
                  name={option.name}
                  key={`${option.name}-${idx}`}
                  ref={(el) => (focusItem.current[idx] = el)}
                  data-testid={'menu-dropdown-button-' + idx}
                  className="w-full cursor-pointer flex align-left p-2  hover:underline"
                  onClick={() => selectItem(option.name)}
                  onKeyDown={keyDownHandler}
                >
                  <span className="w-4 h-4 mt-1 mr-1 flex self-center justify-center">{option.icon()}</span>
                  {option.name}
                </button>
              ))}
            </div>
          </div>
        </FocusTrap>
      )}
    </div>
  );
};
